import { createHmac } from 'node:crypto';
import type {
  L402AgentOptions,
  BalanceInfo,
  Offer,
  CreateOfferParams,
  Contract,
  FundResult,
  DepositInvoice,
  DepositStatus,
  LedgerEntry,
  WithdrawResult,
  AgentRegistration,
  PaymentNeededCallback,
  ListOffersParams,
  TenantInfo,
  TenantReputation,
  ContractReceipt,
  ContractReceiptVerificationResult,
  ServiceCard,
  ServiceCardVerificationResult,
  CreateServiceCardOptions,
  WalletPolicy,
  WalletPolicyContext,
  WalletPolicyDecision,
  WalletPolicySpendRequest,
  FundContractPolicyOptions,
  ContractActionOptions,
  ContractNextAction,
  ContractRole,
  WaitForContractActionOptions,
  ContractEvent,
  DepositEvent,
  WatchOptions,
} from './types.js';
import { createContractReceipt, verifyContractReceipt } from './receipts.js';
import { createServiceCard, verifyServiceCard } from './service-cards.js';
import { evaluateWalletPolicy } from './wallet-policies.js';
import { getContractNextAction } from './contract-actions.js';

export class L402Error extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'L402Error';
    this.status = status;
    this.code = code;
  }
}

// ---------- SSE helpers ----------

interface SseFrame {
  event?: string;
  data: string;
  id?: string;
}

const TERMINAL_CONTRACT_STATUSES = new Set([
  'released',
  'refunded',
  'expired',
  'settled',
]);

function isTerminalContractStatus(status: string | undefined | null): boolean {
  return !!status && TERMINAL_CONTRACT_STATUSES.has(status);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      signal?.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Parse a `text/event-stream` ReadableStream into SseFrame objects.
 * Comment lines (starting with `:`) are surfaced as `keepalive` frames so
 * callers can update last-seen timestamps. AbortSignal cancels the stream.
 */
async function* parseSseFrames(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal
): AsyncGenerator<SseFrame & { keepalive?: boolean }> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const onAbort = () => {
    reader.cancel().catch(() => {});
  };
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    while (true) {
      if (signal?.aborted) return;
      const { value, done } = await reader.read();
      if (done) {
        if (buffer.trim().length > 0) {
          const frame = parseSseBlock(buffer);
          if (frame) yield frame;
        }
        return;
      }
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are delimited by a blank line.
      let idx: number;
      while ((idx = indexOfDoubleNewline(buffer)) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + (buffer[idx] === '\r' ? 4 : 2));
        const frame = parseSseBlock(raw);
        if (frame) yield frame;
      }
    }
  } finally {
    signal?.removeEventListener('abort', onAbort);
    try { await reader.cancel(); } catch { /* noop */ }
  }
}

function indexOfDoubleNewline(s: string): number {
  const a = s.indexOf('\n\n');
  const b = s.indexOf('\r\n\r\n');
  if (a === -1) return b;
  if (b === -1) return a;
  return Math.min(a, b);
}

function parseSseBlock(block: string): (SseFrame & { keepalive?: boolean }) | null {
  const lines = block.split(/\r?\n/);
  let event: string | undefined;
  let id: string | undefined;
  const dataParts: string[] = [];
  let sawComment = false;

  for (const line of lines) {
    if (line === '') continue;
    if (line.startsWith(':')) {
      sawComment = true;
      continue;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') event = value;
    else if (field === 'data') dataParts.push(value);
    else if (field === 'id') id = value;
    // ignore retry/unknown for now
  }

  if (dataParts.length === 0) {
    if (sawComment) return { event: 'keepalive', data: '', keepalive: true };
    return null;
  }
  return { event, data: dataParts.join('\n'), id };
}

function frameToContractEvent(
  frame: SseFrame & { keepalive?: boolean },
  contractId: string
): ContractEvent | null {
  if (frame.keepalive) {
    return {
      type: 'keepalive',
      contractId,
      data: null,
      timestamp: new Date().toISOString(),
      source: 'sse',
    };
  }
  let parsed: any = null;
  if (frame.data) {
    try { parsed = JSON.parse(frame.data); } catch { parsed = frame.data; }
  }
  const eventName = (frame.event || 'snapshot') as ContractEvent['type'];
  const type: ContractEvent['type'] =
    eventName === 'snapshot' || eventName === 'state_change' || eventName === 'action'
      ? eventName
      : 'state_change';
  const data = parsed?.data ?? parsed;
  const ev: ContractEvent = {
    type,
    contractId: parsed?.contractId ?? parsed?.data?.id ?? contractId,
    data,
    timestamp: parsed?.timestamp ?? new Date().toISOString(),
    source: 'sse',
    event_id: frame.id ?? (parsed?.event_id !== undefined ? String(parsed.event_id) : undefined),
  };
  // Lift optional state_change context fields out of `data` so callers can
  // read them without reaching into the loosely-typed payload. The fields
  // remain on `data` as well for backwards compatibility.
  if (data && typeof data === 'object') {
    if (typeof (data as any).previous_status === 'string') ev.previous_status = (data as any).previous_status;
    if (typeof (data as any).via === 'string') ev.via = (data as any).via;
    if (typeof (data as any).reason === 'string') ev.reason = (data as any).reason;
  }
  return ev;
}

function frameToDepositEvent(
  frame: SseFrame & { keepalive?: boolean },
  paymentHash: string
): DepositEvent | null {
  if (frame.keepalive) {
    return {
      type: 'keepalive',
      paymentHash,
      data: null,
      timestamp: new Date().toISOString(),
      source: 'sse',
    };
  }
  let parsed: any = null;
  if (frame.data) {
    try { parsed = JSON.parse(frame.data); } catch { parsed = frame.data; }
  }
  const eventName = (frame.event || 'snapshot') as DepositEvent['type'];
  const type: DepositEvent['type'] =
    eventName === 'snapshot' || eventName === 'state_change' ? eventName : 'state_change';
  const data = parsed?.data ?? parsed;
  const ev: DepositEvent = {
    type,
    paymentHash: parsed?.paymentHash ?? paymentHash,
    data,
    timestamp: parsed?.timestamp ?? new Date().toISOString(),
    source: 'sse',
    event_id: frame.id ?? (parsed?.event_id !== undefined ? String(parsed.event_id) : undefined),
  };
  if (data && typeof data === 'object') {
    if (typeof (data as any).previous_status === 'string') ev.previous_status = (data as any).previous_status;
    if (typeof (data as any).via === 'string') ev.via = (data as any).via;
    if (typeof (data as any).reason === 'string') ev.reason = (data as any).reason;
  }
  return ev;
}

/**
 * Sleep with exponential backoff + jitter for SSE reconnects.
 *
 * delay = min(maxDelay, base * 2^attempt) + jitter(0..500)
 */
function computeBackoffMs(attempt: number, baseMs: number, maxMs: number): number {
  const exp = Math.min(maxMs, baseMs * Math.pow(2, attempt));
  const jitter = Math.floor(Math.random() * 500);
  return exp + jitter;
}

// ---------- L402Agent ----------

export class L402Agent {
  private apiKey: string;
  private apiUrl: string;

  private onPaymentNeeded?: PaymentNeededCallback;
  private paymentTimeoutMs: number;
  private paymentPollIntervalMs: number;
  private walletPolicy?: WalletPolicy;
  private onPolicyApprovalNeeded?: L402AgentOptions['onPolicyApprovalNeeded'];

  constructor(options: L402AgentOptions) {
    if (!options.apiKey) {
      throw new Error('apiKey is required');
    }
    this.apiKey = options.apiKey;
    this.apiUrl = options.apiUrl ?? 'https://l402gw.nosaltres2.info';
    this.onPaymentNeeded = options.onPaymentNeeded;
    this.paymentTimeoutMs = options.paymentTimeoutMs ?? 300_000;
    this.paymentPollIntervalMs = options.paymentPollIntervalMs ?? 5_000;
    this.walletPolicy = options.walletPolicy;
    this.onPolicyApprovalNeeded = options.onPolicyApprovalNeeded;
  }

  private async request<T>(method: string, path: string, body?: any, auth = true): Promise<T> {
    const url = `${this.apiUrl}${path}`;
    const bodyString = body === undefined ? '' : JSON.stringify(body);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (auth) {
      const timestamp = Date.now().toString();
      const signingPayload = `${method}\n${path}\n${timestamp}\n${bodyString}`;
      headers['X-L402-Key'] = this.apiKey;
      headers['X-L402-Timestamp'] = timestamp;
      headers['X-L402-Signature'] = createHmac('sha256', this.apiKey).update(signingPayload).digest('hex');
    }
    const options: RequestInit = {
      method,
      headers,
    };

    if (body !== undefined) {
      options.body = bodyString;
    }

    const res = await fetch(url, options);

    if (!res.ok) {
      let errorMsg = `HTTP ${res.status}`;
      let errorCode: string | undefined;

      try {
        const data = await res.json() as { error?: string; code?: string };
        errorMsg = data.error || errorMsg;
        errorCode = data.code;
      } catch {
        // Use default error message
      }

      throw new L402Error(errorMsg, res.status, errorCode);
    }

    return res.json() as Promise<T>;
  }

  private buildQuery(params?: object): string {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params || {})) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        query.append(key, String(value));
      }
    }
    const encoded = query.toString();
    return encoded ? `?${encoded}` : '';
  }

  // Static: register a new agent (no auth needed)
  static async register(options: {
    name: string;
    description?: string;
    wallet_type?: 'custodial' | 'external';
    lightning_address?: string;
    apiUrl?: string;
  }): Promise<AgentRegistration> {
    const apiUrl = options.apiUrl ?? 'https://l402gw.nosaltres2.info';
    const url = `${apiUrl}/api/v1/agents/register`;

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: options.name,
        description: options.description,
        wallet_type: options.wallet_type ?? 'custodial',
        lightning_address: options.lightning_address,
      }),
    });

    if (!res.ok) {
      let errorMsg = `HTTP ${res.status}`;
      try {
        const data = await res.json() as { error?: string };
        errorMsg = data.error || errorMsg;
      } catch {
        // Use default error message
      }
      throw new L402Error(errorMsg, res.status);
    }

    return res.json() as Promise<AgentRegistration>;
  }

  // Wallet
  async getBalance(): Promise<BalanceInfo> {
    return this.request('GET', '/api/v1/wallet/balance');
  }

  /**
   * Low-level: create a deposit invoice. Returns the invoice for manual handling.
   * Most agents should use `deposit()` instead, which notifies the human and waits.
   */
  async createDeposit(amount_sats: number): Promise<DepositInvoice> {
    const result = await this.request<DepositInvoice>('POST', '/api/v1/wallet/deposit', { amount_sats });
    // Add pay_url for easy wallet linking
    return {
      ...result,
      pay_url: `lightning:${result.invoice}`,
    };
  }

  /**
   * Check if a deposit invoice has been paid.
   */
  async checkDeposit(paymentHash: string): Promise<DepositStatus> {
    return this.request('GET', `/api/v1/wallet/deposit/${paymentHash}`);
  }

  /**
   * High-level deposit: creates invoice, notifies human, waits for payment.
   *
   * WHY THIS EXISTS: AI agents don't have Lightning wallets. They can't pay
   * invoices. When an agent needs sats, it must ask a human to pay.
   *
   * This method:
   * 1. Creates a Lightning invoice for the requested amount
   * 2. Calls `onPaymentNeeded` so you can notify the human (chat, email, UI)
   * 3. Polls until the invoice is paid or times out
   * 4. Returns the confirmed deposit status
   *
   * If `onPaymentNeeded` is not configured, throws with the invoice so the
   * caller can handle notification manually.
   *
   * @param amount_sats - Amount to deposit
   * @param reason - Human-readable reason (shown in the notification)
   * @returns Confirmed deposit status
   * @throws {L402Error} if payment times out or fails
   *
   * @example
   * const agent = new L402Agent({
   *   apiKey: 'sk_...',
   *   onPaymentNeeded: async (invoice) => {
   *     // Send to Slack, Discord, Signal, email, etc.
   *     await notify(`Pay ${invoice.amount_sats} sats: ${invoice.invoice}`);
   *   },
   * });
   *
   * // Agent requests funding and waits
   * const deposit = await agent.deposit(1000, 'Need funds to accept code-review offer');
   * console.log(`Funded! Balance: ${deposit.amount_sats} sats`);
   */
  async deposit(amount_sats: number, reason?: string): Promise<DepositStatus> {
    const invoice = await this.createDeposit(amount_sats);

    // If no callback, throw with invoice details so caller can handle it
    if (!this.onPaymentNeeded) {
      throw new L402Error(
        `Payment needed: ${amount_sats} sats. ` +
        `Invoice: ${invoice.invoice}. ` +
        `No onPaymentNeeded callback configured — pay this invoice manually ` +
        `and call checkDeposit('${invoice.payment_hash}') to confirm.`,
        402,
        'PAYMENT_NEEDED'
      );
    }

    // Notify the human with clear payment instructions
    const enrichedInvoice: DepositInvoice = {
      ...invoice,
      message: [
        reason
          ? `⚡ Agent needs ${amount_sats} sats: ${reason}`
          : `⚡ Agent needs ${amount_sats} sats deposited`,
        '',
        `📱 Tap to pay: ${invoice.pay_url}`,
        '',
        `Or paste this invoice into any Lightning wallet:`,
        invoice.invoice,
      ].join('\n'),
    };
    await this.onPaymentNeeded(enrichedInvoice);

    // Poll for payment
    if (this.paymentTimeoutMs === 0) {
      return { status: 'pending', amount_sats, paid_at: null };
    }

    // Backwards-compatible: internally we now consume watchDeposit, which
    // uses SSE when the gateway supports it and falls back to polling when
    // the endpoint 404s. Return shape is unchanged.
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), this.paymentTimeoutMs);
    try {
      for await (const event of this.watchDeposit(invoice.payment_hash, { signal: ac.signal })) {
        if (event.type === 'keepalive') continue;
        const data = event.data || {};
        if (data.status === 'paid') {
          return {
            status: 'paid',
            amount_sats: data.amount_sats ?? amount_sats,
            paid_at: data.paid_at ?? new Date().toISOString(),
          };
        }
        if (data.status === 'expired') {
          throw new L402Error('Deposit invoice expired before payment', 408, 'PAYMENT_EXPIRED');
        }
      }
    } catch (err) {
      if ((err as any)?.name === 'AbortError') {
        // fall through to timeout error below
      } else {
        throw err;
      }
    } finally {
      clearTimeout(timer);
    }

    throw new L402Error(
      `Payment not received within ${this.paymentTimeoutMs / 1000}s. ` +
      `Invoice may still be valid — check with checkDeposit('${invoice.payment_hash}')`,
      408,
      'PAYMENT_TIMEOUT'
    );
  }

  /**
   * Ensure the agent has at least `minBalance` sats. If not, request a deposit.
   * Convenience method that checks balance first, then deposits the difference.
   *
   * @example
   * // Before accepting an offer, make sure you can pay
   * await agent.ensureBalance(500, 'Need funds for code-review contract');
   * await agent.fundContract(contractId);
   */
  async ensureBalance(minBalance: number, reason?: string): Promise<BalanceInfo> {
    const current = await this.getBalance();
    if (current.balance_sats >= minBalance) return current;

    const needed = minBalance - current.balance_sats;
    await this.deposit(needed, reason ?? `Need ${needed} more sats (have ${current.balance_sats}, need ${minBalance})`);
    return this.getBalance();
  }

  async withdraw(amount_sats?: number): Promise<WithdrawResult> {
    return this.request('POST', '/api/v1/wallet/withdraw', amount_sats ? { amount_sats } : {});
  }

  // Offers
  async createOffer(params: CreateOfferParams): Promise<Offer> {
    return this.request('POST', '/api/v1/offers', {
      title: params.title,
      description: params.description,
      price_sats: params.price_sats,
      service_type: params.service_type,
      terms: {
        sla_minutes: params.sla_minutes ?? 30,
        dispute_window_minutes: params.dispute_window_minutes ?? 1440,
      },
    });
  }

  async getTenant(): Promise<TenantInfo> {
    return this.request('GET', '/api/v1/tenants/me');
  }

  async getReputation(tenantId?: string): Promise<TenantReputation> {
    const id = tenantId ?? (await this.getTenant()).tenant_id;
    return this.request('GET', `/api/v1/reputation/${encodeURIComponent(id)}`);
  }

  async listOffers(filters?: ListOffersParams): Promise<Offer[]> {
    const result = await this.request<{ offers: Offer[] }>('GET', `/api/v1/offers${this.buildQuery(filters)}`);
    return result.offers || [];
  }

  async browseOffers(filters?: ListOffersParams): Promise<Offer[]> {
    const result = await this.request<{ offers: Offer[] }>(
      'GET',
      `/api/v1/offers${this.buildQuery(filters)}`,
      undefined,
      false
    );
    return result.offers || [];
  }

  async getOffer(offerId: string): Promise<Offer> {
    return this.request('GET', `/api/v1/offers/${offerId}`);
  }

  async getServiceCard(offerId: string, options?: CreateServiceCardOptions): Promise<ServiceCard> {
    const offer = await this.getOffer(offerId);
    let reputation: TenantReputation | null = null;
    try {
      reputation = await this.getReputation(offer.seller_tenant_id);
    } catch {
      reputation = null;
    }
    return createServiceCard(offer, reputation, {
      links: {
        quickstart: 'https://github.com/jordiagi/satonomous/blob/main/examples/first-contract.ts',
        offer: `${this.apiUrl}/api/v1/offers/${encodeURIComponent(offer.id)}`,
      },
      ...options,
    });
  }

  async browseServiceCards(filters?: ListOffersParams, options?: CreateServiceCardOptions): Promise<ServiceCard[]> {
    const offers = await this.browseOffers(filters);
    return offers.map((offer) => createServiceCard(offer, null, options));
  }

  verifyServiceCard(card: ServiceCard): ServiceCardVerificationResult {
    return verifyServiceCard(card);
  }

  async updateOffer(offerId: string, active: boolean): Promise<Offer> {
    return this.request('PATCH', `/api/v1/offers/${offerId}`, { active });
  }

  // Contracts
  async acceptOffer(offerId: string): Promise<Contract> {
    return this.request('POST', '/api/v1/contracts', { offer_id: offerId });
  }

  async fundContract(contractId: string, options: FundContractPolicyOptions = {}): Promise<FundResult> {
    const policy = options.policy ?? this.walletPolicy;
    if (policy) {
      const decision = await this.evaluateContractFunding(contractId, { ...options, policy });
      if (decision.decision === 'deny') {
        throw new L402Error(
          `WalletPolicy denied funding ${contractId}: ${decision.reasons.join('; ')}`,
          403,
          'WALLET_POLICY_DENIED'
        );
      }
      if (decision.decision === 'ask_human' && !options.humanApproved) {
        const approved = await this.onPolicyApprovalNeeded?.(decision);
        if (approved !== true) {
          throw new L402Error(
            `WalletPolicy requires human approval for ${contractId}: ${decision.reasons.join('; ')}`,
            402,
            'WALLET_POLICY_APPROVAL_REQUIRED'
          );
        }
      }
    }
    return this.request('POST', `/api/v1/contracts/${contractId}/fund`, {});
  }

  async listContracts(filters?: { role?: 'buyer' | 'seller'; status?: string }): Promise<Contract[]> {
    let path = '/api/v1/contracts';
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (params.toString()) path += '?' + params.toString();
    const result = await this.request<{ contracts: Contract[] }>('GET', path);
    return result.contracts || [];
  }

  async getContract(contractId: string): Promise<Contract> {
    return this.request('GET', `/api/v1/contracts/${contractId}`);
  }

  /**
   * Open an SSE connection (or fall back to polling) for a contract.
   *
   * Yields `ContractEvent`s including the initial snapshot and any subsequent
   * server-pushed events. The generator terminates when the contract reaches
   * a terminal state, when reconnect attempts are exhausted, or when the
   * caller's AbortSignal fires.
   *
   * Resilience:
   *   - If the gateway responds with anything other than a 2xx
   *     `text/event-stream`, this falls back to repeated `getContract`
   *     polls and synthesizes `snapshot`/`state_change` events.
   *   - If the SSE stream closes mid-flight, this reconnects with
   *     `Last-Event-ID` set to the last `id:` we observed so the gateway's
   *     ring buffer can replay missed events. Backoff: 1s → 2s → ... → 30s
   *     with up to 500ms jitter, capped at `maxReconnectAttempts` (default 10).
   *     After exhaustion the generator yields an `error` event and ends.
   */
  async *watchContract(
    contractId: string,
    options: WatchOptions = {}
  ): AsyncGenerator<ContractEvent> {
    const path = `/api/v1/contracts/${contractId}/events`;
    const maxAttempts = options.maxReconnectAttempts ?? 10;
    const baseDelay = options.baseReconnectDelayMs ?? 1_000;
    const maxDelay = options.maxReconnectDelayMs ?? 30_000;

    let lastEventId: string | null = null;
    let attempt = 0;

    while (!options.signal?.aborted) {
      const stream = await this.openSseStream(path, options.signal, lastEventId);

      if (!stream) {
        // Polling fallback. Yields a synthesized snapshot each tick until the
        // contract reaches a terminal state or the signal aborts.
        const intervalMs = options.pollIntervalMs ?? this.paymentPollIntervalMs;
        let last: string | null = null;
        while (!options.signal?.aborted) {
          const contract = await this.getContract(contractId);
          const fingerprint = `${contract.status}:${contract.completed_at ?? ''}:${contract.released_at ?? ''}:${contract.disputed_at ?? ''}`;
          const isFirst = last === null;
          if (isFirst || fingerprint !== last) {
            last = fingerprint;
            yield {
              type: isFirst ? 'snapshot' : 'state_change',
              contractId: contract.id,
              data: contract,
              timestamp: new Date().toISOString(),
              source: 'poll',
            };
          }
          if (isTerminalContractStatus(contract.status)) return;
          await sleep(intervalMs, options.signal);
        }
        return;
      }

      let frameCount = 0;
      let sawTerminal = false;
      try {
        for await (const frame of parseSseFrames(stream, options.signal)) {
          frameCount++;
          const event = frameToContractEvent(frame, contractId);
          if (event) {
            if (event.event_id) lastEventId = event.event_id;
            yield event;
            if (
              (event.type === 'state_change' || event.type === 'snapshot') &&
              event.data &&
              isTerminalContractStatus(event.data.status)
            ) {
              sawTerminal = true;
              return;
            }
          }
        }
      } finally {
        if (sawTerminal) {
          // already returned
        }
      }

      // Stream ended without a terminal state. Either reconnect or give up.
      if (options.signal?.aborted) return;
      if (frameCount > 0) {
        // Got useful frames, so reset attempt counter — this looked healthy.
        attempt = 0;
      }
      attempt++;
      if (maxAttempts === 0 || attempt > maxAttempts) {
        yield {
          type: 'error',
          contractId,
          data: { message: `SSE reconnect attempts exhausted (${attempt - 1}/${maxAttempts})` },
          timestamp: new Date().toISOString(),
          source: 'sse',
        };
        return;
      }
      const delay = computeBackoffMs(attempt - 1, baseDelay, maxDelay);
      try {
        await sleep(delay, options.signal);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        throw err;
      }
    }
  }

  /**
   * Open an SSE connection (or fall back to polling) for a deposit invoice.
   * Same reconnect-with-backoff semantics as `watchContract`.
   */
  async *watchDeposit(
    paymentHash: string,
    options: WatchOptions = {}
  ): AsyncGenerator<DepositEvent> {
    const path = `/api/v1/wallet/deposit/${paymentHash}/events`;
    const maxAttempts = options.maxReconnectAttempts ?? 10;
    const baseDelay = options.baseReconnectDelayMs ?? 1_000;
    const maxDelay = options.maxReconnectDelayMs ?? 30_000;

    let lastEventId: string | null = null;
    let attempt = 0;

    while (!options.signal?.aborted) {
      const stream = await this.openSseStream(path, options.signal, lastEventId);

      if (!stream) {
        const intervalMs = options.pollIntervalMs ?? this.paymentPollIntervalMs;
        let lastStatus: string | null = null;
        while (!options.signal?.aborted) {
          const status = await this.checkDeposit(paymentHash);
          const isFirst = lastStatus === null;
          if (isFirst || status.status !== lastStatus) {
            lastStatus = status.status;
            yield {
              type: isFirst ? 'snapshot' : 'state_change',
              paymentHash,
              data: status,
              timestamp: new Date().toISOString(),
              source: 'poll',
            };
          }
          if (status.status === 'paid' || status.status === 'expired') return;
          await sleep(intervalMs, options.signal);
        }
        return;
      }

      let frameCount = 0;
      let sawTerminal = false;
      for await (const frame of parseSseFrames(stream, options.signal)) {
        frameCount++;
        const event = frameToDepositEvent(frame, paymentHash);
        if (event) {
          if (event.event_id) lastEventId = event.event_id;
          yield event;
          if (event.data && (event.data.status === 'paid' || event.data.status === 'expired')) {
            sawTerminal = true;
            return;
          }
        }
      }
      if (sawTerminal) return;

      if (options.signal?.aborted) return;
      if (frameCount > 0) attempt = 0;
      attempt++;
      if (maxAttempts === 0 || attempt > maxAttempts) {
        yield {
          type: 'error',
          paymentHash,
          data: { message: `SSE reconnect attempts exhausted (${attempt - 1}/${maxAttempts})` },
          timestamp: new Date().toISOString(),
          source: 'sse',
        };
        return;
      }
      const delay = computeBackoffMs(attempt - 1, baseDelay, maxDelay);
      try {
        await sleep(delay, options.signal);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        throw err;
      }
    }
  }

  /**
   * Issue a signed GET that accepts `text/event-stream`. Returns the response
   * body stream on success, or `null` if the gateway responded with anything
   * else (signaling that the caller should fall back to polling).
   *
   * When `lastEventId` is provided we forward it as the standard SSE
   * `Last-Event-ID` header so the gateway's per-contract DO can replay
   * missed events from its ring buffer.
   */
  private async openSseStream(
    path: string,
    signal?: AbortSignal,
    lastEventId?: string | null
  ): Promise<ReadableStream<Uint8Array> | null> {
    const url = `${this.apiUrl}${path}`;
    const timestamp = Date.now().toString();
    const signingPayload = `GET\n${path}\n${timestamp}\n`;
    const headers: Record<string, string> = {
      'Accept': 'text/event-stream',
      'X-L402-Key': this.apiKey,
      'X-L402-Timestamp': timestamp,
      'X-L402-Signature': createHmac('sha256', this.apiKey).update(signingPayload).digest('hex'),
    };
    if (lastEventId) {
      headers['Last-Event-ID'] = lastEventId;
    }

    let res: Response;
    try {
      res = await fetch(url, { method: 'GET', headers, signal });
    } catch (err) {
      // AbortError propagates; other network errors trigger fallback.
      if ((err as any)?.name === 'AbortError') throw err;
      return null;
    }

    if (!res.ok) {
      // Drain body so the connection can be released, then fall back.
      try { await res.body?.cancel(); } catch { /* noop */ }
      return null;
    }
    const ct = res.headers?.get?.('Content-Type') || '';
    if (!ct.toLowerCase().includes('text/event-stream') || !res.body) {
      try { await res.body?.cancel(); } catch { /* noop */ }
      return null;
    }
    return res.body;
  }

  private async inferRole(contract: Contract): Promise<ContractRole> {
    try {
      const tenant = await this.getTenant();
      if (tenant.tenant_id === contract.buyer_tenant_id) return 'buyer';
      if (tenant.tenant_id === contract.seller_tenant_id) return 'seller';
    } catch {
      // Fall through to observer when tenant lookup is unavailable.
    }
    return 'observer';
  }

  async getContractNextAction(
    contractId: string,
    options: ContractActionOptions = {}
  ): Promise<ContractNextAction> {
    const contract = await this.getContract(contractId);
    const role = options.role ?? (await this.inferRole(contract));
    return getContractNextAction(contract, { ...options, role });
  }

  async listContractActions(
    filters?: { role?: 'buyer' | 'seller'; status?: string },
    options: ContractActionOptions = {}
  ): Promise<ContractNextAction[]> {
    const contracts = await this.listContracts(filters);
    return Promise.all(
      contracts.map(async (contract) => {
        const role = options.role ?? (await this.inferRole(contract));
        return getContractNextAction(contract, { ...options, role });
      })
    );
  }

  async waitForContractAction(
    contractId: string,
    options: WaitForContractActionOptions = {}
  ): Promise<ContractNextAction> {
    const timeoutMs = options.timeoutMs ?? 60_000;
    const pollIntervalMs = options.pollIntervalMs ?? 5_000;

    // Consume the watcher stream (SSE when supported, polling otherwise) and
    // evaluate the next-action helper on each emitted contract snapshot.
    // Return shape is unchanged for backwards compat.
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), timeoutMs);
    try {
      for await (const event of this.watchContract(contractId, {
        signal: ac.signal,
        pollIntervalMs,
      })) {
        if (event.type === 'keepalive' || !event.data) continue;
        const contract = event.data as Contract;
        const role = options.role ?? (await this.inferRole(contract));
        const next = getContractNextAction(contract, { ...options, role });
        if (
          (!options.action || next.action === options.action) &&
          (!options.status || next.status === options.status)
        ) {
          return next;
        }
        if (next.terminal && !options.action && !options.status) return next;
      }
    } catch (err) {
      if ((err as any)?.name !== 'AbortError') throw err;
    } finally {
      clearTimeout(timer);
    }

    throw new L402Error(
      `Timed out waiting for contract ${contractId}` +
      `${options.action ? ` action ${options.action}` : ''}` +
      `${options.status ? ` status ${options.status}` : ''}`,
      408,
      'CONTRACT_WATCH_TIMEOUT'
    );
  }

  evaluateWalletPolicy(
    policy: WalletPolicy,
    request: WalletPolicySpendRequest,
    context?: WalletPolicyContext
  ): WalletPolicyDecision {
    return evaluateWalletPolicy(policy, request, context);
  }

  async evaluateContractFunding(
    contractId: string,
    options: FundContractPolicyOptions = {}
  ): Promise<WalletPolicyDecision> {
    const policy = options.policy ?? this.walletPolicy;
    if (!policy) {
      throw new Error('No WalletPolicy configured. Pass options.policy or set walletPolicy in the constructor.');
    }

    const contract = await this.getContract(contractId);
    const terms = contract.terms_snapshot && typeof contract.terms_snapshot === 'object'
      ? contract.terms_snapshot
      : {};
    const amount = contract.price_sats + contract.fee_sats;
    return evaluateWalletPolicy(
      policy,
      {
        amount_sats: amount,
        price_sats: contract.price_sats,
        fee_sats: contract.fee_sats,
        counterparty_tenant_id: contract.seller_tenant_id,
        service_type: typeof terms.service_type === 'string' ? terms.service_type : null,
        offer_id: contract.offer_id,
        contract_id: contract.id,
        description: typeof terms.title === 'string' ? terms.title : undefined,
      },
      options.context
    );
  }

  // Delivery
  async submitDelivery(contractId: string, proofUrl: string, proofData?: any): Promise<Contract> {
    return this.request('POST', `/api/v1/contracts/${contractId}/deliver`, {
      proof_url: proofUrl,
      proof_data: proofData,
    });
  }

  async confirmDelivery(contractId: string): Promise<Contract> {
    return this.request('POST', `/api/v1/contracts/${contractId}/confirm`, {});
  }

  async disputeDelivery(contractId: string, reason: string, evidenceUrl?: string): Promise<Contract> {
    return this.request('POST', `/api/v1/contracts/${contractId}/dispute`, {
      reason,
      evidence_url: evidenceUrl,
    });
  }

  // Ledger
  async getLedger(limit?: number, offset?: number): Promise<{ balance_sats: number; entries: LedgerEntry[] }> {
    let path = '/api/v1/ledger';
    const params = new URLSearchParams();
    if (limit) params.append('limit', String(limit));
    if (offset) params.append('offset', String(offset));
    if (params.toString()) path += '?' + params.toString();
    return this.request('GET', path);
  }

  async getContractReceipt(contractId: string): Promise<ContractReceipt> {
    const contract = await this.getContract(contractId);
    const { entries } = await this.getLedger(100);
    return createContractReceipt(contract, entries, {
      links: {
        quickstart: 'https://github.com/jordiagi/satonomous/blob/main/examples/first-contract.ts',
      },
    });
  }

  verifyContractReceipt(receipt: ContractReceipt): ContractReceiptVerificationResult {
    return verifyContractReceipt(receipt);
  }
}
